import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { Briefcase, FileDown, ArrowRight, User, MapPin, Phone, Calendar, FileText, DollarSign, Zap, Activity, Layers, Hash } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { useAppContext } from '../../hooks/useAppContext';
import { Role } from '../../types';

interface QuotationItem {
    category: 'الأفضل' | 'الجيد' | 'الاقتصادي' | 'الأدنى';
    horsepower: number;
    capacity_kw: number;
    price_per_kw: number;
    total_before_tax: number;
    vat15: number;
    total_with_tax: number;
    device_type?: string;
    device_count?: number;
    voltage?: number;
}

const ModernQuotationDetailScreen: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user, t } = useAppContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [customerName, setCustomerName] = useState('');
    const [location, setLocation] = useState('');
    const [mobile, setMobile] = useState('');
    const [quoteNumber, setQuoteNumber] = useState<string>('');
    const [quoteDate, setQuoteDate] = useState<string>('');
    const [items, setItems] = useState<QuotationItem[]>([]);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const fetchQuotation = async () => {
            if (!id) return;
            try {
                setLoading(true);
                setError(null);
                const res = await fetch(`${API_BASE_URL}/quotations/${id}`);
                if (!res.ok) throw new Error(`خطأ في جلب العرض: ${res.status}`);
                const data = await res.json();
                setCustomerName(data.customer_name || '');
                setLocation(data.location || '');
                setMobile(data.mobile || '');
                setQuoteNumber(String(data.quote_number || ''));
                setQuoteDate(String(data.quote_date || ''));
                setItems((data.items || []) as QuotationItem[]);
            } catch (e: any) {
                setError(e?.message || 'تعذر جلب العرض');
            } finally {
                setLoading(false);
            }
        };
        fetchQuotation();
    }, [id]);

    const generatePDF = async () => {
        const doc = new jsPDF('p', 'pt', 'a4');

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.left = '-10000px';
        container.style.top = '0';
        container.style.width = '794px';
        container.style.height = '1123px';
        container.style.padding = '40px';
        container.style.direction = 'rtl';
        container.style.fontFamily = 'Cairo, Arial, sans-serif';
        container.style.color = '#353536';
        container.id = 'print-root';
        const d = new Date(quoteDate);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const arDate = d.toLocaleDateString('ar-SA');
        const projectLoc = String(location || '').replace(/\s+/g, '');
        const serial = (() => {
            const digits = String(quoteNumber || '').replace(/\D/g, '');
            const last3 = digits ? digits.slice(-3) : '';
            return last3 ? last3.padStart(3, '0') : '001';
        })();
        const formattedId = `${projectLoc}${yyyy}${mm}${dd}${serial}`;
        const formattedIdHtml = `<span class="accent-location">${projectLoc}</span>${yyyy}${mm}${dd}${serial}`;
        const headHtml = `
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;align-items:center;margin-top:18px;direction:ltr">
        <div style="text-align:left"></div>
        <div style="text-align:center"></div>
        <div></div>
      </div>
      <div style="border:1px solid #e0e0e0;border-radius:8px;padding:10px;margin-top:79px;background:rgba(255,255,255,0.9);width:86%;margin-left:auto;margin-right:auto">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:12px">
          <div>اسم العميل: ${customerName || '-'}</div>
          <div>الموقع: ${location || '-'}</div>
          <div>رقم الجوال: ${mobile || '-'}</div>
          <div style="direction:rtl;unicode-bidi:embed">${formattedIdHtml}</div>
        </div>
      </div>
    `;
        const bgImgHtml = `<img src="/QSREPORT.png.png" crossorigin="anonymous" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:-1" />`;
        const fontCss = `<style>@font-face{font-family:'Cairo';src:url('/fonts/Cairo-Regular.ttf') format('truetype');font-weight:normal;font-style:normal}#print-root, #print-root * { color:#353536 !important }.accent-location{ color:#d00 !important }</style>`;
        const agreedHp = items.length === 1
            ? (items[0].horsepower ?? 0)
            : ((items.find(q => q.category === 'الأفضل')?.horsepower) ?? (items[0].horsepower ?? 0));
        const agreedType = items.length === 1
            ? (((items[0] as any).device_type) || 'رأس كهربائي')
            : ((((items.find(q => q.category === 'الأفضل') as any)?.device_type) || ((items[0] as any).device_type) || 'رأس كهربائي'));
        const agreedCount = items.length === 1
            ? (Number(((items[0] as any).device_count) || 1))
            : (Number((((items.find(q => q.category === 'الأفضل') as any)?.device_count) || ((items[0] as any).device_count) || 1)));
        const introHtml = `
      <div style="margin-top:32px;font-size:12px;line-height:1.6;font-weight:700;margin-right:64px;width:88%">
        <div style="margin-right:12px">المكرم/ ${customerName || '-'}</div>
        <div style="margin-right:12px">تحية طيبة.</div>
        <div>بناء على الاجتماع الذي عقد معكم لمناقشة آلية تشغيل عدد (<span style="color:#353536">${Number(agreedCount)}</span>) ${agreedType} بقدرة <span style="color:#353536">${Number(agreedHp || 0).toFixed(0)}</span> حصان على الطاقة الشمسية، نرفق لكم عرض السعر يتضمن الجوانب الفنية والتشغيلية</div>
      </div>
    `;
        const tableSingle = (q: any) => `
      <table style="width:100%;border-collapse:collapse;margin-top:116px;font-size:12px">
        <thead>
          <tr style="background:#f79156;color:#353536"><th style="padding:8px;text-align:center">البيان</th><th style="padding:8px;text-align:center">القيمة</th><th style="padding:8px;text-align:center"></th></tr>
        </thead>
        <tbody>
          <tr><td style="padding:8px;text-align:right">عدد الأحصنة (HP)</td><td style="padding:8px;text-align:center">${Number(q.horsepower ?? 0).toFixed(0)}</td><td></td></tr>
          <tr><td style="padding:8px;text-align:right">القدرة (kW)</td><td style="padding:8px;text-align:center">${Number(q.capacity_kw ?? 0).toFixed(0)}</td><td></td></tr>
          <tr><td style="padding:8px;text-align:right">الإجمالي قبل الضريبة</td><td style="padding:8px;text-align:center">${q.total_before_tax} ر.س</td><td></td></tr>
          <tr><td style="padding:8px;text-align:right">ضريبة القيمة المضافة 15%</td><td style="padding:8px;text-align:center">${q.vat15} ر.س</td><td></td></tr>
          <tr style="background:#f5f5f5"><td style="padding:8px;text-align:right">الإجمالي شامل الضريبة</td><td style="padding:8px;text-align:center">${q.total_with_tax} ر.س</td><td></td></tr>
        </tbody>
        <tfoot>
          <tr><td style="padding:8px;text-align:right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td><td></td><td style="padding:8px;text-align:center">${(q as any).device_count ?? 1}</td></tr>
          <tr><td style="padding:8px;text-align:right">المحول</td><td></td><td style="padding:8px;text-align:center">${(q as any).voltage ?? 380}V</td></tr>
        </tfoot>
      </table>
    `;
        const tableMulti = (arr: any[]) => `
      <table style="width:100%;border-collapse:collapse;margin-top:76px;font-size:12px">
        <thead>
          <tr style="background:#f79156;color:#353536">
            <th style="padding:8px;text-align:center">الفئة</th>
            <th style="padding:8px;text-align:center">HP</th>
            <th style="padding:8px;text-align:center">kW</th>
            <th style="padding:8px;text-align:center">الإجمالي قبل الضريبة</th>
            <th style="padding:8px;text-align:center">الإجمالي شامل الضريبة</th>
            <th style="padding:8px;text-align:center"></th>
          </tr>
        </thead>
        <tbody>
          ${arr.map(q => `
            <tr>
              <td style="padding:8px;background:#f5f5f5;text-align:center">${q.category}</td>
              <td style="padding:8px;text-align:center">${Number(q.horsepower ?? 0).toFixed(0)}</td>
              <td style="padding:8px;text-align:center">${Number(q.capacity_kw ?? 0).toFixed(0)}</td>
              <td style="padding:8px;text-align:center">${q.total_before_tax} ر.س</td>
              <td style="padding:8px;text-align:center">${q.total_with_tax} ر.س</td>
              <td></td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr><td colspan="5" style="padding:8px;text-align:right">عدد المضخات المراد تشغيلها على الطاقة الشمسية</td><td style="padding:8px;text-align:center">${(arr[0] as any)?.device_count ?? 1}</td></tr>
          <tr><td colspan="5" style="padding:8px;text-align:right">المحول</td><td style="padding:8px;text-align:center">${380}V</td></tr>
        </tfoot>
      </table>
    `;
        const paymentsHtml = ``;
        const notesHtml = ``;
        container.innerHTML = fontCss + bgImgHtml + headHtml + introHtml + (items.length === 1 ? tableSingle(items[0]) : tableMulti(items)) + paymentsHtml + notesHtml;
        document.body.appendChild(container);
        const canvas = await html2canvas(container, { scale: 2, useCORS: true });
        const dataUrl = canvas.toDataURL('image/png');
        document.body.removeChild(container);
        doc.addImage(dataUrl, 'PNG', 0, 0, pageWidth, pageHeight);
        doc.save(`quotation_${formattedId || 'offer'}.pdf`);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                    <p className="text-slate-400 animate-pulse">جاري تحميل العرض...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center max-w-md">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500">
                        <Activity size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">تعذر تحميل العرض</h3>
                    <p className="text-slate-400 mb-6">{error}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors"
                    >
                        عودة
                    </button>
                </div>
            </div>
        );
    }

    const total = items.reduce((sum, it) => sum + Number(it.total_with_tax || 0), 0);

    const _d = quoteDate ? new Date(quoteDate) : new Date();
    const _yyyy = _d.getFullYear();
    const _mm = String(_d.getMonth() + 1).padStart(2, '0');
    const _dd = String(_d.getDate()).padStart(2, '0');
    const _projectLoc = String(location || '').replace(/\s+/g, '');
    const _serial = (() => {
        const digits = String(quoteNumber || '').replace(/\D/g, '');
        const last3 = digits ? digits.slice(-3) : '';
        return last3 ? last3.padStart(3, '0') : '001';
    })();

    return (
        <div className="max-w-5xl mx-auto space-y-8 animate-content-fade-in pb-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-blue-500/20 text-blue-400">
                            <Briefcase size={28} />
                        </div>
                        تفاصيل عرض السعر
                    </h1>
                    <p className="text-slate-400">استعراض وتصدير تفاصيل عرض السعر</p>
                </div>
                <button
                    onClick={() => navigate(user?.role === Role.Admin ? "/admin-quotations" : "/quotations/new")}
                    className="p-3 rounded-full bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
                >
                    <ArrowRight size={24} className="rotate-180" />
                </button>
            </div>

            {/* Customer Info Card */}
            <div className="glass-panel p-6 rounded-2xl space-y-6">
                <h2 className="text-xl font-bold text-white border-b border-slate-700/50 pb-4 flex items-center gap-2">
                    <User size={20} className="text-blue-400" />
                    بيانات العميل
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                        <div className="text-slate-400 text-xs mb-1 flex items-center gap-1">
                            <User size={12} /> اسم العميل
                        </div>
                        <div className="text-white font-semibold text-lg">{customerName || '-'}</div>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                        <div className="text-slate-400 text-xs mb-1 flex items-center gap-1">
                            <MapPin size={12} /> الموقع
                        </div>
                        <div className="text-white font-semibold text-lg">{location || '-'}</div>
                    </div>
                    <div className="bg-slate-900/50 p-4 rounded-xl border border-slate-700/50">
                        <div className="text-slate-400 text-xs mb-1 flex items-center gap-1">
                            <Phone size={12} /> رقم الجوال
                        </div>
                        <div className="text-white font-semibold text-lg" dir="ltr">{mobile || '-'}</div>
                    </div>
                </div>
            </div>

            {/* Quotation Details Card */}
            <div className="glass-panel p-6 rounded-2xl space-y-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-700/50 pb-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <FileText size={20} className="text-purple-400" />
                        تفاصيل العرض
                    </h2>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <div className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 border border-slate-700 flex items-center gap-2">
                            <Hash size={14} className="text-slate-500" />
                            <span>رقم العرض:</span>
                            <span dir="rtl" className="font-mono text-blue-400 font-bold">
                                <span className="text-red-400">{_projectLoc}</span>{_yyyy}{_mm}{_dd}{_serial}
                            </span>
                        </div>
                        <div className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-300 border border-slate-700 flex items-center gap-2">
                            <Calendar size={14} className="text-slate-500" />
                            <span>التاريخ:</span>
                            <span className="font-bold">{quoteDate ? new Date(quoteDate).toLocaleDateString('ar-SA') : '-'}</span>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {items.length === 1 ? (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-800/50 text-slate-400">
                                <tr>
                                    <th className="p-3 text-right rounded-r-xl">البيان</th>
                                    <th className="p-3 text-left rounded-l-xl">القيمة</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                <tr><td className="p-3 text-slate-300">الفئة</td><td className="p-3 text-left font-semibold text-white">{items[0].category}</td></tr>
                                <tr><td className="p-3 text-slate-300">النوع</td><td className="p-3 text-left font-semibold text-white">{(items[0] as any).device_type ?? 'رأس كهربائي'}</td></tr>
                                <tr><td className="p-3 text-slate-300">العدد</td><td className="p-3 text-left font-semibold text-white">{Number((items[0] as any).device_count ?? 1)}</td></tr>
                                <tr><td className="p-3 text-slate-300">عدد الأحصنة (HP)</td><td className="p-3 text-left font-semibold text-white">{items[0].horsepower ?? 0}</td></tr>
                                <tr><td className="p-3 text-slate-300">القدرة (kW)</td><td className="p-3 text-left font-semibold text-white">{items[0].capacity_kw}</td></tr>
                                <tr><td className="p-3 text-slate-300">الفولت</td><td className="p-3 text-left font-semibold text-white">{Number((items[0] as any).voltage ?? 380)}V</td></tr>
                                <tr><td className="p-3 text-slate-300">السعر/كيلوواط</td><td className="p-3 text-left font-semibold text-white">{items[0].price_per_kw} ر.س</td></tr>
                                <tr><td className="p-3 text-slate-300">الإجمالي قبل الضريبة</td><td className="p-3 text-left font-semibold text-white">{items[0].total_before_tax} ر.س</td></tr>
                                <tr><td className="p-3 text-slate-300">ضريبة القيمة المضافة (15%)</td><td className="p-3 text-left font-semibold text-white">{items[0].vat15} ر.س</td></tr>
                                <tr className="bg-blue-500/10"><td className="p-3 text-blue-300 font-bold">الإجمالي شامل الضريبة</td><td className="p-3 text-left font-bold text-blue-400 text-lg">{items[0].total_with_tax} ر.س</td></tr>
                            </tbody>
                        </table>
                    ) : (
                        <table className="w-full text-sm text-center">
                            <thead className="bg-slate-800/50 text-slate-400">
                                <tr>
                                    <th className="p-3 rounded-r-xl">الفئة</th>
                                    <th className="p-3">النوع</th>
                                    <th className="p-3">العدد</th>
                                    <th className="p-3">HP</th>
                                    <th className="p-3">kW</th>
                                    <th className="p-3">الفولت</th>
                                    <th className="p-3">السعر/kW</th>
                                    <th className="p-3 rounded-l-xl">الإجمالي</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/50">
                                {items.map((q, i) => (
                                    <tr key={i} className="hover:bg-slate-800/30 transition-colors">
                                        <td className="p-3 font-semibold text-white">{q.category}</td>
                                        <td className="p-3 text-slate-300">{(q as any).device_type ?? 'رأس كهربائي'}</td>
                                        <td className="p-3 text-slate-300">{Number((q as any).device_count ?? 1)}</td>
                                        <td className="p-3 text-slate-300">{q.horsepower ?? 0}</td>
                                        <td className="p-3 text-slate-300">{q.capacity_kw}</td>
                                        <td className="p-3 text-slate-300">{Number((q as any).voltage ?? 380)}V</td>
                                        <td className="p-3 text-slate-300">{q.price_per_kw}</td>
                                        <td className="p-3 font-bold text-emerald-400">{q.total_with_tax} ر.س</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="flex flex-col md:flex-row justify-between items-center gap-6 pt-6 border-t border-slate-700/50">
                    <div className="flex items-center gap-3 bg-slate-900/50 px-6 py-3 rounded-xl border border-slate-700/50">
                        <div className="p-2 rounded-full bg-emerald-500/20 text-emerald-400">
                            <DollarSign size={20} />
                        </div>
                        <div>
                            <div className="text-xs text-slate-400">الإجمالي النهائي</div>
                            <div className="text-xl font-bold text-white">{total} <span className="text-sm font-normal text-slate-500">ر.س</span></div>
                        </div>
                    </div>

                    <button
                        onClick={async () => { setExporting(true); try { await generatePDF(); } finally { setExporting(false); } }}
                        disabled={exporting}
                        className="flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25 transition-all font-bold disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto justify-center"
                    >
                        {exporting ? (
                            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        ) : (
                            <>
                                <FileDown size={20} />
                                <span>تصدير PDF</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModernQuotationDetailScreen;
